import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('prayersReadings');
export interface PrayersReadingsRec {
  id: string;
  userId: string;
  religiousPreference?: string;
  specificPrayers?: string;
  scriptureReadings?: string;
  poemsReadings?: string;
  preferredReaders?: string;
  languagePreference?: string;
  culturalTraditions?: string;
  specialBlessings?: string;
  avoidContent?: string;
  notes?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

class PrayersReadingsService {
  async getAllPrayersReadings(): Promise<PrayersReadingsRec[]> {
    const response = await http.get('/api/prayers-readings');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch prayers and readings');
    }

    return response.data.data || [];
  }

  async getPrayersReadingsById(id: string): Promise<PrayersReadingsRec | null> {
    try {
      const response = await http.get(`/api/prayers-readings/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch prayers and readings');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching prayers and readings by ID:', error);
      return null;
    }
  }

  async createPrayersReadings(prayersReadingsData: Partial<PrayersReadingsRec>): Promise<PrayersReadingsRec> {
    const response = await http.post('/api/prayers-readings', prayersReadingsData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save prayers and readings');
    }

    return response.data.data;
  }

  async updatePrayersReadings(id: string, prayersReadingsData: Partial<PrayersReadingsRec>): Promise<PrayersReadingsRec> {
    const response = await http.patch(`/api/prayers-readings/${id}`, prayersReadingsData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update prayers and readings');
    }

    return response.data.data;
  }

  async deletePrayersReadings(id: string): Promise<void> {
    const response = await http.delete(`/api/prayers-readings/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete prayers and readings');
    }
  }

  async lockPrayersReadings(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for prayers and readings ID:', id);
    const response = await http.post(`/api/prayers-readings/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock prayers and readings');
    }
  }

  async unlockPrayersReadings(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for prayers and readings ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/prayers-readings/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock prayers and readings');
    }
  }

  async getPrayersReadingsAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/prayers-readings/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch prayers and readings access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching prayers and readings access grants:', error);
      return [];
    }
  }

  async deletePrayersReadingsAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/prayers-readings/${id}/access-grants/${grantId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }
}

export const prayersReadingsService = new PrayersReadingsService();
